import {
  Modal,
  Box,
  Typography,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { useState } from "react";
import CloseButton from "../buttons/CloseButton";
import { DefaultPrimaryButton } from "@/styles/globalstyles";
import type {
  ParsedImportResult,
  ImportedChildPart,
  ImportedPartProcessRow,
  ImportedPartOtherProcessRow,
  ImportWarning,
} from "@/utils/excel";

type PreviewTab =
  | "child-part"
  | "material-cost"
  | "child-part-process"
  | "part-process"
  | "part-other-process";

interface ImportPreviewModalProps {
  open: boolean;
  data: ParsedImportResult | null;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

const TAB_LABELS: { key: PreviewTab; label: string }[] = [
  { key: "child-part", label: "Child Part Info" },
  { key: "material-cost", label: "Material Cost" },
  { key: "child-part-process", label: "Child Part Process" },
  { key: "part-process", label: "Part Process" },
  { key: "part-other-process", label: "Part Other Process" },
];

const errorCell = {
  backgroundColor: "#ffebee",
  color: "#d32f2f",
  fontWeight: 600,
};

const ambiguousCell = {
  backgroundColor: "#fff8e1",
  color: "#ed6c02",
  fontWeight: 600,
};

const findWarning = (
  warnings: ImportWarning[],
  type: ImportWarning["type"],
  childPartNo?: string,
  processName?: string,
): ImportWarning | undefined =>
  warnings.find(
    (w) =>
      w.type === type &&
      (!childPartNo || w.childPartNo === childPartNo) &&
      (!processName || w.processName === processName),
  );

const fmtNum = (value: number | string | null | undefined): number | string => {
  if (typeof value !== "number") return value ?? "-";
  return parseFloat(value.toPrecision(10));
};

const WarningIcon = ({ warning }: { warning: ImportWarning }) => (
  <Tooltip title={warning.message} arrow placement="top">
    <Warning
      sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle", cursor: "help" }}
    />
  </Tooltip>
);

// ─── Header Info ─────────────────────────────────────────────────────────────

const HeaderInfo = ({
  data,
  warnings,
}: {
  data: ParsedImportResult;
  warnings: ImportWarning[];
}) => {
  const h = data.preview.header;
  const partNotFoundWarn = warnings.find((w) => w.type === "part-not-found");
  const fields = [
    { label: "Part No.", value: h.part_no },
    { label: "Part Name", value: h.part_name },
    { label: "AC", value: h.ac },
    { label: "RT", value: h.rt },
  ];

  return (
    <>
      <Box display="flex" gap={3} flexWrap="wrap" mt={1} mb={2}>
        {fields.map((f) => (
          <Box key={f.label}>
            <Typography variant="caption" color="textSecondary">
              {f.label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={
                partNotFoundWarn && f.label === "Part No."
                  ? errorCell
                  : undefined
              }
            >
              {f.value || "-"}
              {partNotFoundWarn && f.label === "Part No." && (
                <WarningIcon warning={partNotFoundWarn} />
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};

// ─── Child Part Info Table ───────────────────────────────────────────────────

const ChildPartTable = ({
  parts,
  warnings,
}: {
  parts: ImportedChildPart[];
  warnings: ImportWarning[];
}) => (
  <TableContainer
    component={Paper}
    variant="outlined"
    sx={{ maxHeight: "55vh" }}
  >
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
            Child Part No
          </TableCell>
          <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
            Child Part Name
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Qty
          </TableCell>
          <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>
            Material Code
          </TableCell>
          <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
            Material Supplier
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Material Type</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Spec</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Dimension</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>2nd Tier Supplier</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>1st Tier Supplier</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {parts.map((cp, i) => {
          const matWarn = findWarning(
            warnings,
            "material-not-found",
            cp.child_part_no,
          );
          const ambiguousWarn = findWarning(
            warnings,
            "material-ambiguous",
            cp.child_part_no,
          );
          const legacyResolvedWarn = findWarning(
            warnings,
            "material-resolved-legacy",
            cp.child_part_no,
          );
          const softWarn = ambiguousWarn ?? legacyResolvedWarn;
          return (
            <TableRow key={cp.child_part_no + i}>
              <TableCell>{cp.child_part_no}</TableCell>
              <TableCell>{cp.child_part_name}</TableCell>
              <TableCell align="right">{fmtNum(cp.qty)}</TableCell>
              <TableCell
                sx={matWarn ? errorCell : softWarn ? ambiguousCell : undefined}
              >
                {cp.material_code || "-"}
                {matWarn && <WarningIcon warning={matWarn} />}
                {!matWarn && softWarn && <WarningIcon warning={softWarn} />}
              </TableCell>
              <TableCell>
                {cp.material_supplier || "-"}
              </TableCell>
              <TableCell>{cp.material_type || "-"}</TableCell>
              <TableCell>{cp.material_spec || "-"}</TableCell>
              <TableCell>{cp.dimension || "-"}</TableCell>
              <TableCell>{cp.second_tier_supplier || "-"}</TableCell>
              <TableCell>{cp.first_tier_supplier || "-"}</TableCell>
            </TableRow>
          );
        })}
        {parts.length === 0 && (
          <TableRow>
            <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
              ไม่มีข้อมูล
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Material Cost Table ─────────────────────────────────────────────────────

const MaterialCostTable = ({
  parts,
  warnings,
}: {
  parts: ImportedChildPart[];
  warnings: ImportWarning[];
}) => {
  const uniqueSecondTierNames = [
    ...new Set(
      parts.flatMap((p) => p.second_tier_processes.map((x) => x.name)),
    ),
  ];
  const uniqueOtherNames = [
    ...new Set(parts.flatMap((p) => p.other_processes.map((x) => x.name))),
  ];
  const totalCols = 15 + uniqueSecondTierNames.length + uniqueOtherNames.length;

  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ maxHeight: "55vh" }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
              Child Part No
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Amount
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Ratio
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Consumption
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Part Weight
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Material Cost
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">
              Purchased
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Admin
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Profit
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                minWidth: 160,
                bgcolor: "secondary.light",
              }}
              align="right"
            >
              2nd Tier Total
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                minWidth: 100,
                bgcolor: "secondary.light",
              }}
            >
              Comment
            </TableCell>
            {uniqueSecondTierNames.map((name) => (
              <TableCell
                key={name}
                sx={{
                  fontWeight: 700,
                  minWidth: 120,
                  bgcolor: "primary.light",
                }}
                align="right"
              >
                {name}
              </TableCell>
            ))}
            <TableCell
              sx={{
                fontWeight: 700,
                minWidth: 160,
                bgcolor: "secondary.light",
              }}
              align="right"
            >
              Other Process Total
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                minWidth: 100,
                bgcolor: "secondary.light",
              }}
            >
              Comment
            </TableCell>
            {uniqueOtherNames.map((name) => (
              <TableCell
                key={name}
                sx={{
                  fontWeight: 700,
                  minWidth: 120,
                  bgcolor: "primary.light",
                }}
                align="right"
              >
                {name}
              </TableCell>
            ))}
            <TableCell sx={{ fontWeight: 700 }} align="right">
              %
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parts.map((cp, i) => {
            const secondTierTotal = cp.second_tier_processes.reduce(
              (s, p) => s + p.value,
              0,
            );
            const otherProcessTotal = cp.other_processes.reduce(
              (s, p) => s + p.value,
              0,
            );
            return (
              <TableRow key={cp.child_part_no + i}>
                <TableCell>{cp.child_part_no}</TableCell>
                <TableCell align="right">{fmtNum(cp.amount)}</TableCell>
                <TableCell align="right">{fmtNum(cp.ratio)}</TableCell>
                <TableCell align="right">{fmtNum(cp.consumption)}</TableCell>
                <TableCell align="right">{fmtNum(cp.part_weight)}</TableCell>
                <TableCell align="right">{fmtNum(cp.material_cost)}</TableCell>
                <TableCell align="center">
                  {cp.is_purchased ? "Yes" : "No"}
                </TableCell>
                <TableCell align="right">{fmtNum(cp.admin)}</TableCell>
                <TableCell align="right">{fmtNum(cp.profit)}</TableCell>
                <TableCell align="right">{fmtNum(secondTierTotal)}</TableCell>
                <TableCell
                  sx={{
                    color: cp.second_tier_process_comment
                      ? "text.primary"
                      : "text.secondary",
                    fontStyle: cp.second_tier_process_comment
                      ? "normal"
                      : "italic",
                  }}
                >
                  {cp.second_tier_process_comment || "-"}
                </TableCell>
                {uniqueSecondTierNames.map((name) => {
                  const warn = findWarning(
                    warnings,
                    "material-cost-process-not-found",
                    undefined,
                    name,
                  );
                  return (
                    <TableCell
                      key={name}
                      align="right"
                      sx={warn ? errorCell : undefined}
                    >
                      {fmtNum(
                        cp.second_tier_processes.find((p) => p.name === name)
                          ?.value ?? 0,
                      )}
                      {warn && <WarningIcon warning={warn} />}
                    </TableCell>
                  );
                })}
                <TableCell align="right">{fmtNum(otherProcessTotal)}</TableCell>
                <TableCell
                  sx={{
                    color: cp.other_process_comment
                      ? "text.primary"
                      : "text.secondary",
                    fontStyle: cp.other_process_comment ? "normal" : "italic",
                  }}
                >
                  {cp.other_process_comment || "-"}
                </TableCell>
                {uniqueOtherNames.map((name) => {
                  const warn = findWarning(
                    warnings,
                    "material-cost-process-not-found",
                    undefined,
                    name,
                  );
                  return (
                    <TableCell
                      key={name}
                      align="right"
                      sx={warn ? errorCell : undefined}
                    >
                      {fmtNum(
                        cp.other_processes.find((p) => p.name === name)
                          ?.value ?? 0,
                      )}
                      {warn && <WarningIcon warning={warn} />}
                    </TableCell>
                  );
                })}
                <TableCell align="right">{fmtNum(cp.percent)}</TableCell>
              </TableRow>
            );
          })}
          {parts.length === 0 && (
            <TableRow>
              <TableCell colSpan={totalCols} align="center" sx={{ py: 3 }}>
                ไม่มีข้อมูล
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ─── Child Part Process Table ────────────────────────────────────────────────

const ChildPartProcessTable = ({
  parts,
  warnings,
}: {
  parts: ImportedChildPart[];
  warnings: ImportWarning[];
}) => (
  <TableContainer
    component={Paper}
    variant="outlined"
    sx={{ maxHeight: "55vh" }}
  >
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
            Child Part No
          </TableCell>
          <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
            Process Name
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Qty 1
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Cost/Qty 1
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Qty 2
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Cost/Qty 2
          </TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Tooling</TableCell>
          <TableCell sx={{ fontWeight: 700 }} align="right">
            Tooling Cost
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {parts.flatMap((cp) =>
          cp.cp_processes.length > 0
            ? cp.cp_processes.map((proc, j) => {
                const procWarn = findWarning(
                  warnings,
                  "child-part-process-not-found",
                  undefined,
                  proc.name,
                );
                return (
                  <TableRow key={`${cp.child_part_no}-cp-${j}`}>
                    <TableCell>{j === 0 ? cp.child_part_no : ""}</TableCell>
                    <TableCell sx={procWarn ? errorCell : undefined}>
                      {proc.name}
                      {procWarn && <WarningIcon warning={procWarn} />}
                    </TableCell>
                    <TableCell>{proc.detail || "-"}</TableCell>
                    <TableCell align="right">{fmtNum(proc.qty_1)}</TableCell>
                    <TableCell align="right">
                      {fmtNum(proc.cost_per_qty_1)}
                    </TableCell>
                    <TableCell align="right">{fmtNum(proc.qty_2)}</TableCell>
                    <TableCell align="right">
                      {fmtNum(proc.cost_per_qty_2)}
                    </TableCell>
                    <TableCell>{proc.tooling_name || "-"}</TableCell>
                    <TableCell align="right">
                      {proc.tooling_cost || "-"}
                    </TableCell>
                  </TableRow>
                );
              })
            : [
                <TableRow key={`${cp.child_part_no}-empty`}>
                  <TableCell>{cp.child_part_no}</TableCell>
                  <TableCell colSpan={8} sx={{ color: "text.secondary" }}>
                    ไม่มี Process
                  </TableCell>
                </TableRow>,
              ],
        )}
        {parts.length === 0 && (
          <TableRow>
            <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
              ไม่มีข้อมูล
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

// ─── Part Process Table ──────────────────────────────────────────────────────

const PartProcessTable = ({
  title,
  color,
  processes,
  admin,
  profit,
  warnings,
}: {
  title: string;
  color: string;
  processes: ImportedPartProcessRow[];
  admin: number;
  profit: number;
  warnings: ImportWarning[];
}) => (
  <Paper variant="outlined" sx={{ mb: 2 }}>
    <Box
      sx={{ bgcolor: color, px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}
    >
      <Typography fontWeight={600} fontSize={14}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 1 }}>
      <Box display="flex" gap={3} mb={1} px={1}>
        <Typography variant="body2" color="textPrimary">
          Admin: <strong>{admin}</strong>
        </Typography>
        <Typography variant="body2" color="textPrimary">
          Profit: <strong>{profit}</strong>
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 40 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
              Process Name
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Unit
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Price/Unit
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Total
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processes.map((p, i) => {
            const procWarn = findWarning(
              warnings,
              "part-process-not-found",
              undefined,
              p.name,
            );
            return (
              <TableRow key={i}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={procWarn ? errorCell : undefined}>
                  {p.name}
                  {procWarn && <WarningIcon warning={procWarn} />}
                </TableCell>
                <TableCell>{p.detail || "-"}</TableCell>
                <TableCell align="right">{fmtNum(p.unit)}</TableCell>
                <TableCell align="right">{fmtNum(p.price_per_unit)}</TableCell>
                <TableCell align="right">{fmtNum(p.total)}</TableCell>
              </TableRow>
            );
          })}
          {processes.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={6}
                align="center"
                sx={{ py: 2, color: "text.secondary" }}
              >
                ไม่มีข้อมูล
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  </Paper>
);

// ─── Part Other Process Table ────────────────────────────────────────────────

const PartOtherProcessTable = ({
  title,
  color,
  processes,
  admin,
  profit,
  warnings,
}: {
  title: string;
  color: string;
  processes: ImportedPartOtherProcessRow[];
  admin: number;
  profit: number;
  warnings: ImportWarning[];
}) => (
  <Paper variant="outlined" sx={{ mb: 2 }}>
    <Box
      sx={{ bgcolor: color, px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}
    >
      <Typography fontWeight={600} fontSize={14}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 1 }}>
      <Box display="flex" gap={3} mb={1} px={1}>
        <Typography variant="body2" color="textPrimary">
          Admin: <strong>{admin}</strong>
        </Typography>
        <Typography variant="body2" color="textPrimary">
          Profit: <strong>{profit}</strong>
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, minWidth: 40 }}>#</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
              Process Name
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">
              Price
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processes.map((p, i) => {
            const procWarn = findWarning(
              warnings,
              "part-other-process-not-found",
              undefined,
              p.name,
            );
            return (
              <TableRow key={i}>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={procWarn ? errorCell : undefined}>
                  {p.name}
                  {procWarn && <WarningIcon warning={procWarn} />}
                </TableCell>
                <TableCell align="right">{fmtNum(p.price)}</TableCell>
              </TableRow>
            );
          })}
          {processes.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={3}
                align="center"
                sx={{ py: 2, color: "text.secondary" }}
              >
                ไม่มีข้อมูล
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  </Paper>
);

// ─── Main Modal ──────────────────────────────────────────────────────────────

const ImportPreviewModal = ({
  open,
  data,
  onClose,
  onConfirm,
  isSubmitting,
}: ImportPreviewModalProps) => {
  const [activeTab, setActiveTab] = useState<PreviewTab>("child-part");

  if (!data) return null;

  const { preview, warnings } = data;
  // material-ambiguous / material-resolved-legacy are auto-resolved (not a failure) — show but don't block submit
  const blockingWarnings = warnings.filter(
    (w) => w.type !== "material-ambiguous" && w.type !== "material-resolved-legacy",
  );
  const warningCount = blockingWarnings.length;
  const ambiguousCount = warnings.filter((w) => w.type === "material-ambiguous").length;
  const legacyResolvedCount = warnings.filter((w) => w.type === "material-resolved-legacy").length;

  const renderTabContent = () => {
    switch (activeTab) {
      case "child-part":
        return (
          <ChildPartTable parts={preview.child_parts} warnings={warnings} />
        );
      case "material-cost":
        return (
          <MaterialCostTable parts={preview.child_parts} warnings={warnings} />
        );
      case "child-part-process":
        return (
          <ChildPartProcessTable
            parts={preview.child_parts}
            warnings={warnings}
          />
        );
      case "part-process":
        return (
          <>
            <PartProcessTable
              title="OEM Part Process"
              color="#e3f2fd"
              processes={preview.part_process.oem_part_process}
              admin={preview.part_process.admin_oem}
              profit={preview.part_process.profit_oem}
              warnings={warnings}
            />
            <PartProcessTable
              title="Spare Part Process"
              color="#fff3e0"
              processes={preview.part_process.spare_part_process}
              admin={preview.part_process.admin_spare}
              profit={preview.part_process.profit_spare}
              warnings={warnings}
            />
          </>
        );
      case "part-other-process":
        return (
          <>
            <PartOtherProcessTable
              title="OEM Part Other Process"
              color="#e3f2fd"
              processes={preview.part_other_process.oem_other_process}
              admin={preview.part_other_process.admin_oem}
              profit={preview.part_other_process.profit_oem}
              warnings={warnings}
            />
            <PartOtherProcessTable
              title="Spare Part Other Process"
              color="#fff3e0"
              processes={preview.part_other_process.spare_other_process}
              admin={preview.part_other_process.admin_spare}
              profit={preview.part_other_process.profit_spare}
              warnings={warnings}
            />
          </>
        );
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95vw", md: "90vw" },
          maxWidth: 1400,
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 1, position: "relative" }}>
          <CloseButton onClick={onClose} />
          <Typography variant="h5" fontWeight={700}>
            Preview
          </Typography>
          <HeaderInfo data={data} warnings={warnings} />
          {warningCount > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              พบ {warningCount} รายการที่มีปัญหา — ตรวจสอบช่องที่แสดงสีแดง
            </Alert>
          )}
          {ambiguousCount > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              พบ {ambiguousCount} รายการที่ Material Code ตรงหลาย attribute — ระบบเลือกให้อัตโนมัติแล้ว ตรวจสอบช่องสีเหลืองก่อนบันทึก
            </Alert>
          )}
          {legacyResolvedCount > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              พบ {legacyResolvedCount} รายการที่ Material Code ไม่ตรง attribute โดยตรง — เทียบกับระบบเก่าแล้วพบตัวที่ตรงกัน ตรวจสอบช่องสีเหลืองก่อนบันทึก
            </Alert>
          )}
          <Divider sx={{ mt: 1 }} />
        </Box>

        {/* Body: sidebar + content */}
        <Box
          sx={{ display: "flex", flex: 1, overflow: "hidden", px: 3, pb: 1 }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              width: 200,
              minWidth: 200,
              pr: 2,
              pt: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {TAB_LABELS.map(({ key, label }) => {
              const isActive = activeTab === key;
              const tabWarnings =
                key === "child-part"
                  ? warnings.filter(
                      (w) =>
                        w.type === "material-not-found" ||
                        w.type === "material-ambiguous" ||
                        w.type === "material-resolved-legacy",
                    )
                  : key === "material-cost"
                    ? warnings.filter(
                        (w) => w.type === "material-cost-process-not-found",
                      )
                    : key === "child-part-process"
                      ? warnings.filter(
                          (w) => w.type === "child-part-process-not-found",
                        )
                      : key === "part-process"
                        ? warnings.filter(
                            (w) => w.type === "part-process-not-found",
                          )
                        : warnings.filter(
                            (w) => w.type === "part-other-process-not-found",
                          );
              return (
                <Button
                  key={key}
                  variant={isActive ? "contained" : "text"}
                  size="small"
                  onClick={() => setActiveTab(key)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontWeight: isActive ? 700 : 400,
                    fontSize: 12,
                    color: isActive ? "#fff" : "text.primary",
                    bgcolor: isActive ? "#00A8A9" : "transparent",
                    "&:hover": {
                      bgcolor: isActive ? "#00A8A9" : "grey.100",
                    },
                  }}
                  endIcon={
                    tabWarnings.length > 0 ? (
                      <Chip
                        label={tabWarnings.length}
                        size="small"
                        color="error"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    ) : undefined
                  }
                >
                  {label}
                </Button>
              );
            })}
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: "auto", pt: 1 }}>
            {renderTabContent()}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="textSecondary">
            Child Parts: {preview.child_parts.length} รายการ
          </Typography>
          <Box display="flex" gap={2}>
            <Button variant="text" onClick={onClose} disabled={isSubmitting}>
              ยกเลิก
            </Button>
            <Button
              variant="contained"
              sx={DefaultPrimaryButton}
              onClick={onConfirm}
              disabled={isSubmitting || warningCount > 0}
            >
              {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImportPreviewModal;
